/**
 * LoveLox — Product Personalizer linked-option price display.
 *
 * Product Personalizer (PP) links option values to variants of hidden
 * "pricing products" and charges them correctly at checkout via Shopify
 * Markets. But PP fetches display prices WITHOUT market context, so on
 * non-GBP storefronts it shows the GBP amount as if it were local currency.
 *
 * This script fetches the real market prices (Storefront API with
 * @inContext) and, read-only towards PP: appends a formatted " (+€8)" suffix
 * to each linked option's name (stripping legacy " (+£14)" / " (+{{{9}}})"
 * texts), appends the chosen option's price (or "Included") to PP's
 * selected-value label, and shows the market-correct total in the
 * personalizer title and next to ADD TO CART, hiding PP's own total via CSS.
 * It never writes data-pplr_price or calls PP's price engine.
 *
 * Pure helpers live on window.LLPP and are unit-tested in
 * tests/ll-pp-option-prices.test.mjs (run: node --test tests/*.test.mjs).
 */
(function (window) {
  'use strict';

  var LLPP = {};

  // --- pure helpers --------------------------------------------------------

  // PP renders data-variant="variantId,productId,handle,sku" and joins
  // multi-selections with "||". All numeric variant ids, duplicates kept.
  LLPP.listVariantIds = function (values) {
    var out = [];
    (values || []).forEach(function (value) {
      if (!value) return;
      String(value).split('||').forEach(function (part) {
        var token = part.split(',')[0].trim();
        if (/^\d+$/.test(token)) out.push(Number(token));
      });
    });
    return out;
  };

  // Unique variant ids, for fetching prices.
  LLPP.parseVariantIds = function (values) {
    var seen = {};
    return LLPP.listVariantIds(values).filter(function (id) {
      if (seen[id]) return false;
      seen[id] = true;
      return true;
    });
  };

  LLPP.buildPriceQuery = function (ids, country) {
    if (!/^[A-Z]{2}$/.test(country || '')) {
      throw new Error('LLPP: invalid country code: ' + country);
    }
    return {
      query:
        'query LLPPPrices($ids: [ID!]!) @inContext(country: ' + country + ') {' +
        ' nodes(ids: $ids) { ... on ProductVariant { id price { amount currencyCode } } } }',
      variables: {
        ids: ids.map(function (id) { return 'gid://shopify/ProductVariant/' + id; }),
      },
    };
  };

  function groupDigits(intPart, thousands) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  }

  function formatNumber(amount, thousands, decimal, forceNoDecimals, keepZeroDecimals) {
    var rounded = Math.round(amount * 100) / 100;
    var noDecimals = forceNoDecimals || (!keepZeroDecimals && Number.isInteger(rounded));
    if (forceNoDecimals) rounded = Math.round(rounded);
    var fixed = rounded.toFixed(noDecimals ? 0 : 2);
    var parts = fixed.split('.');
    var result = groupDigits(parts[0], thousands);
    if (parts[1]) result += decimal + parts[1];
    return result;
  }

  var MONEY_TOKENS = {
    amount: [',', '.', false],
    amount_no_decimals: [',', '.', true],
    amount_with_comma_separator: ['.', ',', false],
    amount_no_decimals_with_comma_separator: ['.', ',', true],
    amount_with_apostrophe_separator: ["'", '.', false],
  };

  // Format an amount using a Shopify money format string. Surcharges trim
  // ",00" on whole amounts ("+€8" reads better than "+€8,00"); totals pass
  // keepZeroDecimals to match PP's "£176.00" style.
  LLPP.formatMoney = function (amount, moneyFormat, keepZeroDecimals) {
    var format = String(moneyFormat || '').replace(/<[^>]*>/g, '');
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match || !MONEY_TOKENS[match[1]]) {
      return formatNumber(amount, ',', '.', false, keepZeroDecimals) + (format.trim() ? ' ' + format.trim() : '');
    }
    var spec = MONEY_TOKENS[match[1]];
    return format.replace(match[0], formatNumber(amount, spec[0], spec[1], spec[2], keepZeroDecimals));
  };

  LLPP.priceSuffix = function (amount, moneyFormat) {
    return ' (+' + LLPP.formatMoney(amount, moneyFormat) + ')';
  };

  // Remove a legacy trailing price suffix: " (+£14)", " (+0 €)", " (+{{{9}}})".
  // Only a trailing parenthesised group starting with "+" is touched.
  LLPP.stripPriceSuffix = function (text) {
    return String(text).replace(/\s*\(\s*\+[^()]*\)\s*$/, '');
  };

  // First non-empty direct text node of a node (keeps child spans intact).
  function textNodeOf(node) {
    if (!node || !node.childNodes) return null;
    for (var i = 0; i < node.childNodes.length; i++) {
      var child = node.childNodes[i];
      if (child.nodeType === 3 && child.textContent.trim()) return child;
    }
    return null;
  }

  // The text node holding an option's visible name:
  //   PP V3: <span class="pplr-drop-item"><span class="pplr-choice-label">Name</span></span>
  //   PP V2 and native <option>: the name is a direct text node.
  //   Checkboxes: the input carries the variant, the name sits in its <label>.
  // Writing only this text node leaves PP's (and Zepto's) child spans alone.
  LLPP.findTextTarget = function (el) {
    var label = el.querySelector ? el.querySelector('.pplr-choice-label') : null;
    var wrapper = !label && el.closest ? el.closest('label') : null;
    if (wrapper) label = wrapper.querySelector('.pplr-choice-label') || wrapper;
    return textNodeOf(label || el);
  };

  // What Shopify will charge for the personalised item: the product variant
  // plus one cart line per linked variant id (PP bills repeats as extra
  // quantity), times the quantity. Null when any price is unknown, so the
  // caller keeps PP's own total instead of showing a wrong one.
  LLPP.computeTotal = function (basePrice, variantIds, prices, quantity) {
    if (typeof basePrice !== 'number' || isNaN(basePrice)) return null;
    var total = basePrice;
    for (var i = 0; i < variantIds.length; i++) {
      if (!(variantIds[i] in prices)) return null;
      total += prices[variantIds[i]];
    }
    return Math.round(total * (quantity || 1) * 100) / 100;
  };

  // True when a priced field is not linked to a variant. PP bills that as a
  // flat fee we can't price per market, so we must not replace PP's total.
  LLPP.hasUnpricedSurcharge = function (fields) {
    return (fields || []).some(function (field) {
      return !field.variant && parseFloat(field.price) > 0;
    });
  };

  // Suffix for PP's selected-value label ('Select your chain - 925 silver |
  // 20-22-24"'). A linked choice shows its variant's market price; an unlinked
  // choice in a field that links other choices is free, so it reads
  // "Included". Null means show nothing: price not loaded yet, a flat PP price
  // we can't localise, or a field with no linked choices (fonts, colours).
  LLPP.selectedSuffix = function (field, prices, moneyFormat, includedLabel) {
    if (field.variantIds.length) {
      var total = 0;
      for (var i = 0; i < field.variantIds.length; i++) {
        if (!(field.variantIds[i] in prices)) return null;
        total += prices[field.variantIds[i]];
      }
      return LLPP.priceSuffix(total, moneyFormat);
    }
    if (field.unpricedSurcharge || !field.hasLinkedChoices || !includedLabel) return null;
    return ' (' + includedLabel + ')';
  };

  // --- DOM adapter (browser only, verified on the storefront) ---------------

  var priceCache = {}; // variantId -> market price (number)
  var pendingFetch = null;
  var resweepQueued = false;

  function fetchPrices(ids, config) {
    var missing = ids.filter(function (id) { return !(id in priceCache); });
    if (!missing.length) return Promise.resolve(priceCache);
    var token = window.pplr_storefront_token;
    if (!token) return Promise.reject(new Error('LLPP: no PP storefront token on page'));
    var body = LLPP.buildPriceQuery(missing, config.country);
    return window
      .fetch('https://' + config.shop + '/api/' + config.apiVersion + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify(body),
      })
      .then(function (res) { return res.json(); })
      .then(function (json) {
        ((json.data || {}).nodes || []).forEach(function (node) {
          if (!node || !node.id || !node.price) return;
          var id = node.id.split('/').pop();
          priceCache[id] = parseFloat(node.price.amount);
        });
        return priceCache;
      });
  }

  // Read-only by design: append the market-correct display suffix to a linked
  // option's own text, and nothing else. We deliberately do NOT write
  // data-pplr_price or call PP's price engine:
  //   - Charging is already correct without us: PP adds each linked variant as
  //     its own cart line, priced per market by Shopify Markets.
  //   - Writing data-pplr_price races with PP's multi-currency ("diffcur")
  //     rounding. It distorts PP's running total (undercounts button options in
  //     non-base currencies) and, when a field still carries a flat price, blows
  //     up the "Personalisation fee" reconciliation. Leaving it alone is both
  //     safer and closer to correct.
  function applyToElement(el, config) {
    if (el.classList.contains('ll-pp-priced')) return;
    var ids = LLPP.parseVariantIds([el.getAttribute('data-variant') || el.getAttribute('data-p-variant')]);
    if (!ids.length) return;
    var total = 0;
    var known = true;
    ids.forEach(function (id) {
      if (id in priceCache) total += priceCache[id];
      else known = false;
    });
    if (!known) return;
    var textNode = LLPP.findTextTarget(el);
    if (!textNode) return; // nothing visible to label yet; the next sweep retries
    el.classList.add('ll-pp-priced');
    textNode.textContent =
      LLPP.stripPriceSuffix(textNode.textContent) + LLPP.priceSuffix(total, config.moneyFormat);
  }

  // Option items only. PP also mirrors the chosen variant onto the field's
  // hidden <input>/<select>, which has no visible text to label.
  var ITEM_SELECTOR =
    '[data-variant]:not(.ll-pp-priced):not(input[type="hidden"]):not(select),' +
    '[data-p-variant]:not(.ll-pp-priced)';

  function sweep(config) {
    var elements = document.querySelectorAll(ITEM_SELECTOR);
    if (!elements.length) return;
    var ids = LLPP.parseVariantIds(
      Array.prototype.map.call(elements, function (el) {
        return el.getAttribute('data-variant') || el.getAttribute('data-p-variant');
      })
    );
    if (!ids.length) return;
    if (pendingFetch) {
      // A fetch is in flight; PP may re-render elements meanwhile (it rebuilds
      // option HTML from pre-rewrite snapshots), so queue one more pass.
      resweepQueued = true;
      return;
    }
    pendingFetch = fetchPrices(ids, config)
      .then(function () {
        pendingFetch = null;
        Array.prototype.forEach.call(elements, function (el) { applyToElement(el, config); });
        renderPrices(config);
        // Catch elements (re-)rendered while the fetch was running. With the
        // cache warm this applies synchronously and cannot loop: a second pass
        // only runs when unprocessed elements appeared, and processing marks
        // them ll-pp-priced.
        if (resweepQueued) {
          resweepQueued = false;
          sweep(config);
        }
      })
      .catch(function (err) {
        pendingFetch = null;
        resweepQueued = false;
        if (window.console) console.warn('LLPP price sweep failed:', err);
      });
  }

  // --- total -----------------------------------------------------------------

  // Same selector PP uses to build the linked cart lines (pplr_add_price in
  // canvas-script-with-view-v3.js), so our total matches what Shopify charges.
  var CHARGED_SELECTOR =
    '.pplr-wrapper select[data-variant]:not([data-variant=""]):not([disabled]),' +
    '.pplr-wrapper input[data-variant]:not([data-variant=""]):not([disabled])';

  // root: the whole page, or one field (.pplr-wrapper) for its own share.
  function readChargedVariantIds(root) {
    return LLPP.listVariantIds(
      Array.prototype.map.call(root.querySelectorAll(CHARGED_SELECTOR), function (el) {
        return el.getAttribute('data-variant');
      })
    );
  }

  function readPricedFields(root) {
    return Array.prototype.map.call(root.querySelectorAll('.pplraddprice:not([disabled])'), function (el) {
      return { variant: el.getAttribute('data-variant'), price: el.getAttribute('data-pplr_price') };
    });
  }

  // The product form PP adds to cart from (the one carrying the variant id).
  function atcForm() {
    var forms = document.querySelectorAll('.pplr_atc_form');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('[name="id"]')) return forms[i];
    }
    return forms[0] || null;
  }

  // Selected variant's price in the market currency (pplr_product is the
  // Liquid product JSON rendered in market context, prices in cents).
  function readBasePrice(form) {
    var product = window.pplr_product;
    if (!product || !product.variants || !product.variants.length) return null;
    var input = form && form.querySelector('[name="id"]');
    var id = input ? String(input.value) : '';
    var variant = product.variants.filter(function (v) { return String(v.id) === id; })[0] || product.variants[0];
    return variant.price / 100;
  }

  // Mirrors PP's own quantity lookup (pplrqty).
  function readQuantity(form) {
    if (!form) return 1;
    var input = form.querySelector('[name="quantity"]');
    var formId = form.getAttribute('id');
    if (!input && formId) input = document.querySelector('[name="quantity"][form="' + formId + '"]');
    return Math.max(1, parseInt(input && input.value, 10) || 1);
  }

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text; // no-op writes keep the observer quiet
  }

  // Our own span next to PP's price. It deliberately lacks the "pplrcur"
  // class, which PP's script rewrites; "pplr_cur" only borrows PP's styling.
  function ensureTotalSpan(host, after, extraClass) {
    var span = host.querySelector('.ll-pp-total');
    if (!span) {
      span = document.createElement('span');
      span.className = 'll-pp-total' + (extraClass ? ' ' + extraClass : '');
      host.insertBefore(span, after ? after.nextSibling : null);
    }
    return span;
  }

  // PP 9.622 leaves the modal title without a price, and its ADD TO CART
  // price sums GBP numbers as local currency. We show the real total in both
  // places and hide PP's .pplrcur via CSS (snippets/product-personalizer.liquid).
  // If we can't price everything, PP's own total stays visible.
  function renderTotal(config) {
    var modal = document.querySelector('.pplr_crop-modal');
    if (!modal) return;
    var form = atcForm();
    var base = readBasePrice(form);
    var total = base === null || LLPP.hasUnpricedSurcharge(readPricedFields(document))
      ? null
      : LLPP.computeTotal(base, readChargedVariantIds(document), priceCache, readQuantity(form));
    if (total === null) {
      // PP's own total reappears; never leave a stale one of ours beside it.
      Array.prototype.forEach.call(modal.querySelectorAll('.ll-pp-total'), function (span) {
        span.parentNode.removeChild(span);
      });
      modal.classList.remove('ll-pp-total-active');
      return;
    }
    var text = LLPP.formatMoney(total, config.moneyFormat, true);
    var title = modal.querySelector('.pplr_h_title');
    if (title) setText(ensureTotalSpan(title, title.querySelector('._ps')), text);
    var ppPrice = modal.querySelector('.pplr_ex_inner .pplrcur');
    if (ppPrice) setText(ensureTotalSpan(ppPrice.parentNode, ppPrice, 'pplr_cur'), text);
    modal.classList.add('ll-pp-total-active');
  }

  // --- selected-value label ----------------------------------------------------

  // Fields with PP's "show selected value" setting carry
  // <span class="pplr_option_text_span"> - Silver plated | 16-18"</span> in
  // their label. We append the choice's price as a child span, so it inherits
  // PP's styling. PP rewrites that span with .text() on every selection, which
  // drops our child; the observer then re-adds it with the new choice's price.
  function renderSelectedPrices(config) {
    Array.prototype.forEach.call(document.querySelectorAll('.pplr-wrapper .pplr_option_text_span'), function (span) {
      var ours = span.querySelector('.ll-pp-selected-price');
      var label = textNodeOf(span);
      // PP renders " - " alone when nothing is chosen.
      var chosen = label && label.textContent.replace(/^\s*-\s*/, '').trim();
      var field = span.closest('.pplr-wrapper');
      var suffix = chosen
        ? LLPP.selectedSuffix(
            {
              variantIds: readChargedVariantIds(field),
              unpricedSurcharge: LLPP.hasUnpricedSurcharge(readPricedFields(field)),
              hasLinkedChoices: !!field.querySelector(
                '[data-variant]:not([data-variant=""]), [data-p-variant]:not([data-p-variant=""])'
              ),
            },
            priceCache,
            config.moneyFormat,
            config.includedLabel
          )
        : null;
      if (!suffix) {
        if (ours) span.removeChild(ours);
        return;
      }
      // Option names that still carry a hardcoded price ("Long 20-22-24
      // inches (+£9)") reach this label verbatim; ours replaces it.
      var stripped = LLPP.stripPriceSuffix(label.textContent);
      if (stripped !== label.textContent) label.textContent = stripped;
      if (!ours) {
        ours = document.createElement('span');
        ours.className = 'll-pp-selected-price';
        span.appendChild(ours);
      }
      setText(ours, suffix);
    });
  }

  function renderPrices(config) {
    renderTotal(config);
    renderSelectedPrices(config);
  }

  var debounceTimer = null;

  LLPP.init = function (config) {
    if (!config || !config.country || !config.shop) {
      if (window.console) console.warn('LLPP: missing config, price display disabled');
      return;
    }
    var run = function () {
      sweep(config);
      renderPrices(config); // cache-warm path; sweep re-renders after fetching
    };
    var schedule = function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(run, 150);
    };
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    // Selections change hidden inputs' attributes, which the observer ignores.
    document.addEventListener('change', schedule, true);
    document.addEventListener('click', schedule, true);
    run();
  };

  window.LLPP = LLPP;

  if (typeof document !== 'undefined' && window.llppConfig) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { LLPP.init(window.llppConfig); });
    } else {
      LLPP.init(window.llppConfig);
    }
  }
})(typeof window !== 'undefined' ? window : this);
